import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Package, Tags, MessageSquare, AlertTriangle, ShoppingCart, ArrowRight } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, tone = "blue", to }) => {
  const tones = {
    blue: "bg-brand-blue text-white",
    gold: "bg-brand-gold text-black",
    red: "bg-red-500 text-white",
    slate: "bg-slate-800 text-white",
  };
  const inner = (
    <div className="bg-white border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className={`w-10 h-10 flex items-center justify-center rounded-lg mb-3 ${tones[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-3xl font-black text-slate-900 font-display">{value ?? "—"}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
  return to ? <Link to={to} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{inner}</Link> : inner;
};

export default function Dashboard() {
  const { data: stats } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: () => api.get("/admin/stats").then((r) => r.data),
  });

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display font-black text-3xl text-slate-900 mb-1">Dashboard</h1>
      <p className="text-slate-500 mb-8">Overview of your store.</p>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={ShoppingCart} label="New orders" value={stats?.new_orders} tone="gold" to="/admin/orders" />
        <StatCard icon={Package} label="Active products" value={stats?.active_products} tone="blue" to="/admin/products" />
        <StatCard icon={Tags} label="Categories" value={stats?.categories} tone="slate" to="/admin/categories" />
        <StatCard icon={MessageSquare} label="New enquiries" value={stats?.new_enquiries} tone="blue" to="/admin/enquiries" />
        <StatCard icon={AlertTriangle} label="Low stock" value={stats?.low_stock} tone="red" to="/admin/products" />
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-4">
        <div className="bg-white border rounded-xl p-6">
          <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Quick actions</h2>
          <div className="space-y-2">
            <Link to="/admin/products" className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 border" data-testid="quick-add-product">
              <span className="flex items-center gap-2 font-medium text-slate-700"><Package className="w-4 h-4 text-brand-blue" /> Manage products</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
            <Link to="/admin/enquiries" className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 border" data-testid="quick-enquiries">
              <span className="flex items-center gap-2 font-medium text-slate-700"><MessageSquare className="w-4 h-4 text-brand-blue" /> View enquiries</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
            <Link to="/admin/settings" className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 border" data-testid="quick-settings">
              <span className="flex items-center gap-2 font-medium text-slate-700"><ShoppingCart className="w-4 h-4 text-brand-blue" /> Store settings</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
          </div>
        </div>
        <div className="bg-brand-blue text-white rounded-xl p-6">
          <h2 className="font-display font-bold text-lg mb-2">Web store</h2>
          <p className="text-white/70 text-sm mb-4">
            The online store & Paystack checkout are built but currently hidden from customers. You can enable it later from Settings when you're ready to take online orders.
          </p>
          <p className="text-4xl font-black font-display">{stats?.orders ?? 0}</p>
          <p className="text-white/60 text-sm">orders recorded</p>
        </div>
      </div>
    </div>
  );
}
